import { GtbSidebarSection } from '@core/referential/gtb-sidebar-section.enum'
import { UseCase } from '@core/referential/use-case.enum'
import { createFileRoute, redirect } from '@tanstack/react-router'

import { useAuth } from '@/hooks/use-auth'
import { isUseCase } from '@/lib/utils'
import { companySettingsQuery } from '@/queries/company.settings.query'
import { mySitesQuery } from '@/queries/my-sites.query'

import { SearchSchema as AirQualityConformitySearchSchema } from './_dashboard/air-quality/conformity'
import { SearchSchema as EnergyDashboardSearchSchema } from './_dashboard/energy-usage/dashboard'
import { SearchSchema as ActionsDoneSearchSchema } from './_dashboard/site/$id/actions-done'
import { SearchSchema as AlarmsSearchSchema } from './_dashboard/site/$id/alarms'
import { SearchSchema as MonitoringSearchSchema } from './_dashboard/site/$id/monitoring'

export const Route = createFileRoute('/_auth/')({
  async beforeLoad({ location, context }) {
    const { user } = useAuth.getState()

    if (user) {
      const mySites = await context.queryClient.fetchQuery(mySitesQuery)

      if (mySites.length === 0) {
        throw redirect({
          to: '/403',
          search: {
            reason: 'noSites',
          },
        })
      }

      // `currentSiteId` est une préférence persistée en base, pas une autorisation : elle peut
      // pointer vers un site que l'utilisateur ne peut plus voir (site supprimé, accès retiré, ou
      // company du compte modifiée). La confronter à `mySites` évite de rediriger vers une page
      // inaccessible à chaque connexion, sans possibilité de s'en sortir autrement qu'en tapant une
      // URL à la main.
      const { currentSiteId } = user
      const siteId = currentSiteId !== null && mySites.some(({ id }) => id === currentSiteId) ? currentSiteId : mySites[0].id

      await useAuth.getState().setCurrentSiteId(siteId)

      if (isUseCase(UseCase.LEGIONELLA)) {
        throw redirect({
          to: '/site/$id/actions-done',
          params: { id: siteId },
          search: ActionsDoneSearchSchema.parse({}),
        })
      } else if (isUseCase(UseCase.COLD_STORAGE)) {
        throw redirect({
          to: '/site/$id/monitoring',
          params: { id: siteId },
          search: MonitoringSearchSchema.parse({}),
        })
      } else if (isUseCase(UseCase.ENERGY)) {
        // Le tableau de bord est la page d'atterrissage GTB, sauf pour une company qui a masqué la
        // section : elle retombe sur « Alertes en cours ». Réglages illisibles (erreur réseau,
        // company sans ligne de réglages) = aucune section masquée, comme dans la sidebar.
        const companySettings = await context.queryClient.fetchQuery(companySettingsQuery()).catch(() => null)
        const hiddenSections = companySettings?.sidebarHiddenSections?.[UseCase.ENERGY] ?? []

        if (hiddenSections.includes(GtbSidebarSection.DASHBOARD)) {
          throw redirect({
            to: '/site/$id/alarms',
            params: { id: siteId },
            search: AlarmsSearchSchema.parse({}),
          })
        }

        throw redirect({
          to: '/site/$id/dashboard/general',
          params: { id: siteId },
        })
      } else if (isUseCase(UseCase.CONSUMPTIONS)) {
        throw redirect({
          to: '/energy-usage/dashboard',
          search: EnergyDashboardSearchSchema.parse({}),
        })
      } else if (isUseCase(UseCase.AIR_QUALITY)) {
        throw redirect({
          to: '/air-quality/conformity',
          search: AirQualityConformitySearchSchema.parse({}),
        })
      } else {
        throw redirect({
          to: '/403',
          search: { reason: 'wrongUseCase' },
        })
      }
    }

    if (location.pathname === '/') {
      throw redirect({
        to: '/login',
        replace: true,
      })
    }
  },
})
